import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout';

import {
  catchError,
  combineLatest,
  concatMap,
  delay,
  filter,
  finalize,
  from,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { AuthUser } from '@shared/signup/models';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import {
  ConfirmationModalComponent,
  ConfirmationModalResult,
  NavigationService,
} from '@tablesready/common-ui';
import {
  WhoService,
  PricePlansRestrictionsService,
  PricePlansRestrictionsModalService,
} from '../../../services';
import { ResourcesTabs } from './enums';
import { ResourcesPageService } from './rosources-page.service';

@Component({
  selector: 'tablesready-resources',
  templateUrl: './resources.component.html',
})
export class ResourcesComponent implements OnInit, OnDestroy {
  tabs = [ResourcesTabs.resources];
  activeTab: string;
  disabledTabs = false;
  user: AuthUser;

  private availableForBusinessPlan = false;
  private readonly subscription = new Subscription();

  constructor(
    private readonly service: ResourcesPageService,
    private readonly navService: NavigationService,
    private readonly router: Router,
    private readonly modalController: FrontendModalService,
    private readonly whoService: WhoService,
    private readonly alertsService: AlertsService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
  ) {}

  ngOnInit(): void {
    const baseUrl = '/settings/preferences/resources';
    this.activeTab = ResourcesTabs.resources;

    const tabsSub = this.service.tabsEnabled$.subscribe((tabsStatus) => {
      this.disabledTabs = tabsStatus;
    });
    this.subscription.add(tabsSub);

    const authSub = combineLatest([
      this.authRestaurantService.select(),
      this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.HandsetLandscape]),
    ])
      .pipe(
        tap(([user, { matches: isMobileScreen }]) => {
          this.user = user;
          const floorPlanEnabled = user.account.features.floorPlan;
          const floorPlanTabExists = this.tabs.includes(ResourcesTabs.floors);
          this.availableForBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(user.account.subscriptionType, user.location.recurly);

          if (floorPlanEnabled && !floorPlanTabExists && !isMobileScreen) {
            this.tabs = [ResourcesTabs.resources, ResourcesTabs.floors];
            const url = this.navService.currentUrl.replace(baseUrl, '');
            this.activeTab = url === '/floorplan' ? ResourcesTabs.floors : ResourcesTabs.resources;
          } else if (floorPlanTabExists && (!floorPlanEnabled || isMobileScreen)) {
            this.tabs = [ResourcesTabs.resources];
            this.activeTab = ResourcesTabs.resources;
            void this.router.navigateByUrl(`${baseUrl}/list`);
          }
        }),
        switchMap(() => this.navService.navEvents$),
        tap((navEvent) => {
          if (navEvent.url.includes('/floorplan') && this.user && !this.user.account.features.floorPlan) {
            void this.router.navigateByUrl(`${baseUrl}/list`);
          }
        }),
      )
      .subscribe();

    this.subscription.add(authSub);
  }

  onChangeTab(tab: string): void {
    const user = this.authRestaurantService.get();
    const resourceSettings = user.location.resourceSettings;

    if (!resourceSettings.enabled && tab === ResourcesTabs.floors) {
      if (!this.availableForBusinessPlan) {
        this.confirmEnableResourcesModal(user)
          .pipe(
            take(1),
            filter((res) => res?.response),
            switchMap(() => this.pricePlansRestrictionsModalService.display(this.user)),
            filter(({ isConfirmed }) => !isConfirmed),
            tap(() => this.tabChanged(tab)),
          ).subscribe();

        return;
      }

      this.confirmEnableResourcesModal(user)
        .pipe(
          take(1),
          filter((res) => res?.response),
          concatMap(() => {
            const updateResourcesSettings = {
              enabled: true,
              label: resourceSettings.label || 'Table',
              showOnPages: resourceSettings.showOnPages || false,
              sort: resourceSettings.sort || 'name',
              services: [],
            };

            return this.whoService.updateResourceSettings(updateResourcesSettings);
          }),
          tap(() => this.alertsService.addSuccess('Resources enabled successfully!')),
          catchError(() => {
            this.alertsService.addError('Error enabling resources');

            return of(null);
          }),
          finalize(() => {
            this.tabChanged(tab);
            /*
             TODO: get rid of this shit in ionic branch by investigating data loading
             in builder component and removing request racing
            */
          }),
        ).subscribe();
    } else {
      this.tabChanged(tab);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private tabChanged(tab: string): void {
    let newPage = 'list';
    if (tab === ResourcesTabs.floors) {
      newPage = 'floorplan';
    }

    of(true)
      .pipe(
        delay(1),
        concatMap(() => {
          return from(this.router.navigateByUrl(`settings/preferences/resources/${newPage}`));
        }),
        take(1),
        finalize(() => {
          this.activeTab = tab;
        }),
      )
      .subscribe();
  }

  private confirmEnableResourcesModal(user: AuthUser): Observable<ConfirmationModalResult<boolean>> {
    return this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        title: `Enable ${user.location.resourceSettings.label} Management`,
        content: `Would you like to enable ${user.location.resourceSettings.label} Management in order to create and manage floorplans?`,
        closeBtnText: 'Cancel',
        confirmBtnText: 'Enable',
      },
    });
  }
}
